import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePlaces } from '../context/PlaceContext';

import PrimaryPlaceSelector from '../components/PrimaryPlaceSelector';
import PairingsList from '../components/PairingsList';
import SecondaryPlaceAdder from '../components/SecondaryPlaceAdder';
import HeartIcon from '../components/icons/HeartIcon';

import {
  removePlaceAsFavorite,
  savePlaceAsFavorite,
  fetchPlaceBySlug,
  fetchDistinctMetros
} from '../services/api';

const PlaceSlugPage = () => {
  const { slug } = useParams();
  const { user, getToken } = useAuth();
  const userId = user?.id || null;

  const {
    primaryPlace,
    setPrimaryPlace,
    pairings,
    savedPlaces,
    loadUserFavorites,
    metroFilter,
    setMetroFilter,
    syncPairingsAndVotes,
  } = usePlaces();

  const [errorSlug, setErrorSlug] = useState('');
  const [metros, setMetros] = useState([]);

  const isFavorited = !!savedPlaces.some(
    (fav) => fav.place_internal_id === primaryPlace?.internalId
  );

  // Fetch place by slug
  useEffect(() => {
    if (!slug) return;

    const loadPlace = async () => {
      try {
        setErrorSlug('');
        const data = await fetchPlaceBySlug(slug);
        if (!data?.id) {
          throw new Error('No place found (missing id).');
        }

        if (primaryPlace?.internalId !== data.id) {
          setPrimaryPlace({
            placeId: data.place_id,
            name: data.name,
            address: data.address,
            latitude: data.latitude,
            longitude: data.longitude,
            city: data.city,
            slug: data.slug,
            internalId: data.id,
            metro_area: data.metro_area || null,
          });
        } else {
          // If we're already looking at the same place, re-sync pairings
          await syncPairingsAndVotes(data.id, metroFilter);
        }
      } catch (err) {
        console.error('[PlaceSlugPage] fetchPlaceBySlug error:', err.message);
        setErrorSlug(err.message);
      }
    };

    loadPlace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Fetch distinct metros
  useEffect(() => {
    const loadMetros = async () => {
      try {
        const data = await fetchDistinctMetros();
        setMetros(['All', ...data]);
      } catch (err) {
        console.error('[PlaceSlugPage] fetchMetros error:', err.message);
      }
    };
    loadMetros();
  }, []);

  // Whenever metroFilter changes, re-sync pairings
  useEffect(() => {
    if (primaryPlace?.internalId) {
      syncPairingsAndVotes(primaryPlace.internalId, metroFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metroFilter]);

  if (errorSlug) {
    return <p>Error: {errorSlug}</p>;
  }

  if (!primaryPlace) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <PrimaryPlaceSelector />
      </div>
    );
  }

  const handleToggleFavorite = async () => {
    if (!userId) {
      alert('Please log in to favorite/unfavorite places.');
      return;
    }
    try {
      const token = getToken();
      if (isFavorited) {
        const ok = window.confirm(`Remove "${primaryPlace.name}" from favorites?`);
        if (!ok) return;
        await removePlaceAsFavorite(token, primaryPlace.internalId);
      } else {
        await savePlaceAsFavorite(token, primaryPlace.internalId);
      }
      await loadUserFavorites();
    } catch (err) {
      console.error('[PlaceSlugPage] handleToggleFavorite error:', err.message);
    }
  };

  const handleMetroChange = (e) => {
    setMetroFilter(e.target.value);
  };

  const buildGoogleMapsUrl = (placeName, placeAddress) => {
    const query = encodeURIComponent(`${placeName} ${placeAddress}`);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  const googleMapsUrl = buildGoogleMapsUrl(primaryPlace.name, primaryPlace.address);

  return (
    <div style={{ marginTop: '1rem' }}>
      <PrimaryPlaceSelector />

      {/* Show a banner if this place has no metro_area */}
      {primaryPlace.metro_area === null && (
        <div style={{ backgroundColor: 'pink', padding: '1rem', margin: '1rem 0' }}>
          <strong>sorry!</strong>
          <p>
            We don't support this place's metro area yet. Some features might be unavailable for this place, for now.
          </p>
        </div>
      )}

      <h2>{primaryPlace.name}</h2>
      <p>
        {primaryPlace.address}{' '}
        <span style={{ marginLeft: '0.25rem' }}>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noreferrer"
            className="maps-link"
          >
            (Google Maps)
          </a>
        </span>
      </p>

      <button
        onClick={handleToggleFavorite}
        style={{ 
          border: 'none', 
          background: 'transparent', 
          cursor: 'pointer',
          color: 'var(--dynamic-text)' // Add this line to ensure icon inherits text color
        }}
      >
        <HeartIcon isSelected={isFavorited} />
      </button>

      <div style={{ margin: '1rem 0' }}>
        <label htmlFor="metroSelect">Filter by Metro: </label>
        <select
          id="metroSelect"
          value={metroFilter}
          onChange={handleMetroChange}
        >
          {metros.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      <PairingsList
        pairings={pairings}
        loading={false}
        userId={userId}
        primaryPlaceId={primaryPlace.internalId}
      />

      <SecondaryPlaceAdder />
    </div>
  );
};

export default PlaceSlugPage;